import { AmazonCatalogItem, AmazonCatalogItemDimensions, AmazonListingAttributes, Listing, ListingProductLink, WalmartListingAttributes } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { DataFetcherFunction } from '@/compiler/types';
import { axiosWithoutCaseMiddleware, fetchPaginatedDataFunctionWithMapper } from '@/helpers/forms/axios';

export type ListingRaw = components['schemas']['Listing'];
export type ListingResponse = ListingRaw;

type AmazonListingAttributesRaw = NonNullable<ListingRaw['amazon']>;
type AmazonCatalogItemRaw = NonNullable<AmazonListingAttributesRaw['catalog_item']>;
type AmazonCatalogItemDimensionsRaw = AmazonCatalogItemRaw['item_dimensions'];
type WalmartListingAttributesRaw = NonNullable<ListingRaw['walmart']>;

export type FetchListingsForChannelResponse = operations['list-channel-listings']['responses']['200']['content']['application/json'];
export type CreateListingResponse = operations['create-channel-listing']['responses']['201']['content']['application/json'];
export type LinkListingToProductResponse = operations['link-listing-to-product']['responses']['200']['content']['application/json'];

export type CreateListingRequest = operations['create-channel-listing']['requestBody']['content']['application/json'];
export type LinkListingToProductRequest = operations['link-listing-to-product']['requestBody']['content']['application/json'];

type ListingForChannelRaw = FetchListingsForChannelResponse['data'][number];

function toAmazonCatalogItemDimensions(dimensions: AmazonCatalogItemDimensionsRaw): AmazonCatalogItemDimensions {
    return {
        lengthMm: dimensions.length_mm,
        widthMm: dimensions.width_mm,
        heightMm: dimensions.height_mm,
        weightGm: dimensions.weight_gm,
    };
}

function toAmazonCatalogItem(catalogItem: AmazonCatalogItemRaw): AmazonCatalogItem {
    return {
        marketplaceId: catalogItem.marketplace_id,
        asin: catalogItem.asin,
        title: catalogItem.title,
        associatedIdentifiers: catalogItem.associated_identifiers.map(identifier => ({
            type: identifier.type,
            identifier: identifier.identifier,
        })),
        mainImageUrl: catalogItem.main_image_url,
        thumbnailImageUrl: catalogItem.thumbnail_image_url,
        itemDimensions: toAmazonCatalogItemDimensions(catalogItem.item_dimensions),
        packageDimensions: toAmazonCatalogItemDimensions(catalogItem.package_dimensions),
        isHazmat: catalogItem.is_hazmat,
        updatedAt: catalogItem.updated_at,
    };
}

function toAmazonListingAttributes(attributes: AmazonListingAttributesRaw | null): AmazonListingAttributes | null {
    if (!attributes) return null;

    return {
        fnsku: attributes.fnsku,
        catalogItem: attributes.catalog_item ? toAmazonCatalogItem(attributes.catalog_item) : null,
    };
}

function toWalmartListingAttributes(attributes: WalmartListingAttributesRaw | null): WalmartListingAttributes | null {
    if (!attributes) return null;

    const catalogItem = attributes.catalog_item;
    return {
        wpid: attributes.wpid,
        catalogItem: catalogItem ? {
            uuid: catalogItem.uuid,
            marketplace: catalogItem.marketplace,
            gtin: catalogItem.gtin,
            itemId: catalogItem.item_id,
            associatedIdentifiers: catalogItem.associated_identifiers.map(identifier => ({
                type: identifier.type,
                identifier: identifier.identifier,
            })),
            updatedAt: catalogItem.updated_at,
        } : null,
        wfs: { enabled: attributes.wfs.enabled, offerId: attributes.wfs.offer_id, syncedAt: attributes.wfs.synced_at },
    };
}

export function toListing(listing: ListingRaw): Listing<ListingProductLink> {
    return {
        id: listing.id,
        createdAt: listing.created_at,
        updatedAt: listing.updated_at,
        channelId: listing.channel_id,
        channelType: listing.channel_type,
        sku: listing.sku,
        title: listing.title,
        condition: listing.condition,
        conditionNote: listing.condition_note,
        channelIdentifier: listing.channel_identifier,
        // The listing resource never serialises an item; consumers that need one load it separately.
        item: null,
        status: listing.status,
        identifiers: listing.identifiers.map(identifier => ({
            id: identifier.id,
            listingId: identifier.listing_id,
            identifier: identifier.identifier,
            type: identifier.type,
        })),
        productLink: listing.product_link
            ? {
                id: listing.product_link.id,
                createdAt: listing.product_link.created_at,
                listingId: listing.product_link.listing_id,
                productId: listing.product_link.product_id,
            }
            : null,
        sellerListingDetailsUpdatedAt: listing.seller_listing_details_updated_at,
        amazon: toAmazonListingAttributes(listing.amazon),
        walmart: toWalmartListingAttributes(listing.walmart),
    };
}

export interface UpdateListingRequest {
    channel_id: number
    listing_id: number
    attributes: {
        name: string
        value: unknown
    }[]
}

export interface UpdateListingResponse {
    error: []
    status: string
    message: string
    issues: UpdateListingResponseIssue[]
}

export interface UpdateListingResponseIssue {
    code: string
    message: string
    severity: string
    attribute_names: string[]
}

export interface ListingIssue {
    code: string
    message: string
    severity: 'ERROR' | 'WARNING' | 'INFO'
    attribute_names: string[] | null
}

export interface ListingLiveDataFulfillmentAvailability {
    fulfillmentChannelCode: string
    quantity: number | null
}

export interface ListingLiveData {
    sku: string
    asin: string | null
    fnsku: string | null
    productType: string
    conditionType: string | null
    itemName: string | null
    status: string[]
    mainImageUrl: string | null
    createdDate: string
    lastUpdatedDate: string
    fulfillmentAvailability: ListingLiveDataFulfillmentAvailability[]
}

interface ListingLiveDataResponse {
    sku: string
    asin: string | null
    fnsku: string | null
    product_type: string
    condition_type: string | null
    item_name: string | null
    status: string[]
    main_image_url: string | null
    created_date: string
    last_updated_date: string
    fulfillment_availability: Array<{
        fulfillment_channel_code: string
        quantity: number | null
    }>
}

function toListingLiveData(response: ListingLiveDataResponse): ListingLiveData {
    return {
        sku: response.sku,
        asin: response.asin,
        fnsku: response.fnsku,
        productType: response.product_type,
        conditionType: response.condition_type,
        itemName: response.item_name,
        status: response.status,
        mainImageUrl: response.main_image_url,
        createdDate: response.created_date,
        lastUpdatedDate: response.last_updated_date,
        fulfillmentAvailability: response.fulfillment_availability.map(availability => ({
            fulfillmentChannelCode: availability.fulfillment_channel_code,
            quantity: availability.quantity,
        })),
    };
}

// fetchListings (GET /api/channels/listings) and showListing (GET /api/channels/listings/{listing})
// are undocumented, but the server serialises the same Listing resource as list-channel-listings.
export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): { fetchListings: (preRequest?: (uri: URL) => URL) => DataFetcherFunction<Listing<ListingProductLink>>; fetchListingsForChannel: (channelId: number) => DataFetcherFunction<Listing<ListingProductLink>>; showListing: (listingId: number) => Promise<Listing<ListingProductLink>>; createListing: (channelId: number, payload: CreateListingRequest) => Promise<Listing>; pushListingToAmazon: (listingId: number, asin: string, fulfillmentChannelCode: string) => Promise<void>; updateAmazonListing: (listingId: number, payload: UpdateListingRequest) => Promise<UpdateListingResponse>; linkListingToProduct: (listingId: number, itemId: number) => Promise<void>; unlinkListingFromProduct: (listingId: number) => Promise<void>; getAmazonListingIssues: (listingId: number) => Promise<ListingIssue[]>; fetchListingLiveData: (listingId: number) => Promise<ListingLiveData>; } => ({
    fetchListings: (preRequest?: (uri: URL) => URL): DataFetcherFunction<Listing<ListingProductLink>> => fetchPaginatedDataFunctionWithMapper<ListingRaw, Listing<ListingProductLink>>('/api/channels/listings', toListing, preRequest, axios),

    fetchListingsForChannel: (channelId: number): DataFetcherFunction<Listing<ListingProductLink>> => {
        return fetchPaginatedDataFunctionWithMapper<ListingForChannelRaw, Listing<ListingProductLink>>(`/api/channels/${channelId}/listings`, toListing, undefined, axios);
    },

    showListing: async (listingId: number): Promise<Listing<ListingProductLink>> => {
        const response = await axios.get<{ data: ListingRaw }>(`/api/channels/listings/${listingId}`);

        return toListing(response.data.data);
    },

    createListing: async (channelId: number, payload: CreateListingRequest): Promise<Listing> => {
        const response = await axios.post<CreateListingResponse>(`/api/channels/${channelId}/listings`, payload);

        return toListing(response.data.data);
    },

    pushListingToAmazon: async (listingId: number, asin: string, fulfillmentChannelCode: string): Promise<void> => {
        await axios.post(`/api/channels/amazon/listings/${listingId}`, { asin, fulfillment_channel_code: fulfillmentChannelCode });
    },

    updateAmazonListing: async (listingId: number, payload: UpdateListingRequest): Promise<UpdateListingResponse> => {
        const response = await axios.patch(`/api/channels/amazon/listings/${listingId}`, payload);

        return response.data;
    },

    linkListingToProduct: async (listingId: number, itemId: number): Promise<void> => {
        const body: LinkListingToProductRequest = { item_id: itemId };
        await axios.post<LinkListingToProductResponse>(`/api/channels/listings/${listingId}/link`, body);
    },

    unlinkListingFromProduct: async (listingId: number): Promise<void> => {
        await axios.post(`/api/channels/listings/${listingId}/unlink`);
    },

    getAmazonListingIssues: async (listingId: number): Promise<ListingIssue[]> => {
        const response = await axios.get<{ data: ListingIssue[] }>(`/api/channels/amazon/listings/${listingId}/issues`);
        return response.data.data;
    },

    fetchListingLiveData: async (listingId: number): Promise<ListingLiveData> => {
        const response = await axios.get<{ data: ListingLiveDataResponse }>(`/api/channels/amazon/listings/${listingId}/live-data`);

        return toListingLiveData(response.data.data);
    },
});
